import { create } from 'zustand';

import type { RedirectActionType } from '../components/payment-element/payment.types';
import type { LabelsType, EnvType } from '../@types';
import type { StripeElementLocale } from '@stripe/stripe-js';

type State = {
  env: EnvType;
  lang: StripeElementLocale | string;
  stripeTheme: Record<string, any> | undefined;
  labels?: Record<string, any> | undefined;
  propertyId: number | undefined;
  apiKey: string | undefined;
  baseUrl: string | undefined;
  apiUrl: string | undefined;
  redirectAction: RedirectActionType;
  originalCurrency?: string;
  propertyCurrency?: string;
};

type Action = {
  setStripeTheme: (theme: Record<string, any>) => void;
  setLang: (lang: StripeElementLocale | string) => void;
  setLabels: (labels: LabelsType) => void;
  setPropertyId: (pid: number) => void;
  setApiKey: (apiKey: string) => void;
  setEnv: (env: EnvType) => void;
  setApiUrl: (apiUrl: string) => void;
  setBaseUrl: (baseUrl: string) => void;
  setRedirectAction: (redirectAction: RedirectActionType) => void;
  setOriginalCurrency: (currency: string | undefined) => void;
  setPropertyCurrency: (currency: string | undefined) => void;
};

export type CommonOptionsStoreType = State & Action;

/**
 * Zustand store for common options, which are expected to be set once and not
 * change during the lifecycle of the SDK.
 */
export const useCommonOptionsStore = create<State & Action>()(set => ({
  env: 'prod',
  lang: 'en' as StripeElementLocale,
  stripeTheme: undefined,
  propertyId: undefined,
  apiKey: undefined,
  baseUrl: undefined,
  apiUrl: undefined,
  labels: undefined,
  redirectAction: undefined,
  originalCurrency: undefined,
  propertyCurrency: undefined,
  setEnv: (env: EnvType) => set(() => ({ env })),
  setLang: (lang: StripeElementLocale | string) => set(() => ({ lang })),
  setLabels: labels => set(() => ({ labels })),
  setStripeTheme: (theme: any) => set(() => ({ stripeTheme: theme })),
  setPropertyId: (propertyId: number) => set(() => ({ propertyId })),
  setApiKey: (apiKey: string) => set(() => ({ apiKey })),
  setApiUrl: (apiUrl: string) => set(() => ({ apiUrl })),
  setBaseUrl: (baseUrl: string) => set(() => ({ baseUrl })),
  setRedirectAction: (redirectAction: RedirectActionType) =>
    set(() => ({ redirectAction })),
  setOriginalCurrency: (originalCurrency: string | undefined) =>
    set(() => ({ originalCurrency })),
  setPropertyCurrency: (propertyCurrency: string | undefined) =>
    set(() => ({ propertyCurrency })),
}));
